import classNames from "classnames";
import { useState } from "react";
import { NumberCard } from "../../../../../components/cards/numberCard";
import { TagCard, TagType } from "../../../../../components/cards/tagCard";
import { SearchInput } from "../../../../../components/inputs/searchInput";
import { AddContributionModal } from "../../../../../components/modals/app_modals/contributions/addContributionModal";
import { UploadContributionModal } from "../../../../../components/modals/app_modals/contributions/uploadContributionModal";
import { TransactionDetailsModal } from "../../../../../components/modals/app_modals/transactionDetailsModal";
import { TableComponent } from "../../../../../components/table/table";
import { TimeText } from "../../../../../components/typography/timeText";
import { useModalControl } from "../../../../../customHooks/useModalControl";
import {
  formatToCurrency,
  getChannnelText,
  getOffsetFromPage,
  getPageFromOffset,
  getUploadStatus,
} from "../../../../../helperFunctions";
import { useGetMemberContributionsDetailsQuery } from "../../../../../redux/features/contributions/contributions.api";
import {
  MemberContributionsDetailsQuery,
  SingleContributionLog,
} from "../../../../../redux/features/contributions/contributions.type";
import { SingleCooperativeDetails } from "../../../../../redux/features/cooperative/cooperative.type";

export const CooperativeMemberContributionsView = ({
  cooperativeDetails,
}: {
  cooperativeDetails: SingleCooperativeDetails;
}) => {
  const [selectedEntry, setSelectedEntry] = useState<SingleContributionLog>();
  const [query, setQuery] = useState<MemberContributionsDetailsQuery>({
    coop: cooperativeDetails.id,
    logs: true,
    text: undefined,
    limit: 10,
    offset: 0,
  });

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const {
    data: memberContributions,
    isLoading: loadingMemberContributions,
    isFetching: fetchingMemberContributions,
    refetch: refetchMemberContributions,
  } = useGetMemberContributionsDetailsQuery(query, {
    refetchOnMountOrArgChange: true,
  });

  return (
    <>
      <div className="w-full flex flex-col gap-6">
        <NumberCard
          title={"Contributions Balance"}
          value={memberContributions?.data?.TotalBalance}
          primaryButtonText={"Add"}
          onPrimaryButtonClick={() => {
            handleModalOpen("addContributionModal");
          }}
          primaryButtonVariant="primary-white"
          primaryButtonIcon={"plus"}
          primaryButtonDisabled={false}
          secondaryButtonText={"Upload"}
          onSecondaryButtonClick={() => {
            handleModalOpen("uploadContributionModal");
          }}
          secondaryButtonVariant="secondary-white"
          secondaryButtonIcon={"arrow-up"}
          secondaryButtonDisabled={false}
          loading={loadingMemberContributions}
        />

        <div className="flex items-center flex-wrap gap-2">
          <h3 className="text-base font-medium flex-1">
            Contributions History
          </h3>

          <SearchInput
            placeholder="Search"
            searchFunction={(val) => {
              setQuery({ ...query, text: val });
            }}
            className="w-full md:max-w-[286px] !px-4 !py-2"
            iconDimension={16}
          />
        </div>

        <TableComponent
          className="w-full !min-w-max md:!min-w-full"
          isRowClickable={true}
          mobileCardView={false}
          headers={[]}
          onRowClick={(idx) => {
            setSelectedEntry(memberContributions?.data?.Logs?.results[idx]);
            handleModalOpen("transactionDetailsModal");
          }}
          isLoading={fetchingMemberContributions}
          isTableEmpty={[0, undefined].includes(
            memberContributions?.data?.Logs?.results?.length
          )}
          currentPage={getPageFromOffset(query.offset ?? 0, query.limit ?? 0)}
          isPaginated
          itemsPerPage={query?.limit}
          onPageChange={(page) => {
            setQuery({
              ...query,
              offset: getOffsetFromPage(page, query.limit ?? 0),
            });
          }}
          emptrySrc="/assets/images/empty_state_cooperatives.svg"
          emptyTitle="You've not made any contributions"
          totalCount={memberContributions?.data?.Logs?.count}
          rows={
            memberContributions?.data?.Logs?.results?.map((item, idx) => {
              const key = `$contributions-history-${idx}`;

              return {
                id: item?.id,
                items: [
                  <div
                    key={`${key}-details`}
                    className="flex items-center gap-3 overflow-hidden"
                  >
                    <div className="flex-1">
                      <p
                        className={classNames("text-sm font-bold", {
                          "text-success-600": item?.flow_type === "in",
                          "text-error-600": item?.flow_type === "out",
                        })}
                      >
                        {item?.flow_type === "in" ? "+" : "-"}
                        {formatToCurrency(item.Amount, {
                          decimalPlaces: 2,
                          currency: "NGN",
                        })}
                      </p>

                      <div className="flex items-center gap-1 text-xs text-pry-dark-green-400/60">
                        {getChannnelText(item?.inAppPayment)}
                        <p>∙</p>
                        <TimeText
                          timeString={item?.PaymentDateAdded}
                          showTime={false}
                        />
                      </div>
                    </div>

                    <TagCard
                      text={
                        getUploadStatus(
                          item?.isApprove,
                          item?.ApproveLevel,
                          item.inAppPayment,
                          item.flow_type === "in" ? "credit" : "debit"
                        ).text
                      }
                      type={
                        getUploadStatus(
                          item?.isApprove,
                          item?.ApproveLevel,
                          item.inAppPayment,
                          item.flow_type === "in" ? "credit" : "debit"
                        ).type as TagType
                      }
                    />
                  </div>,
                ],
              };
            }) ?? []
          }
        />
      </div>

      {selectedEntry ? (
        <TransactionDetailsModal
          isModalOpen={modalOpen && modal === "transactionDetailsModal"}
          onModalClose={() => {
            setSelectedEntry(undefined);
            handleModalClose();
          }}
          transaction={selectedEntry}
          type={"contribution"}
        />
      ) : undefined}

      <AddContributionModal
        modal={modal}
        modalOpen={modalOpen}
        handleModalClose={handleModalClose}
        handleModalOpen={handleModalOpen}
        cooperativeDetails={cooperativeDetails}
        onSuccess={async () => {
          await refetchMemberContributions();
        }}
      />

      <UploadContributionModal
        modal={modal}
        modalOpen={modalOpen}
        handleModalClose={handleModalClose}
        handleModalOpen={handleModalOpen}
        cooperativeDetails={cooperativeDetails}
        onSuccess={async () => {
          await refetchMemberContributions();
        }}
      />
    </>
  );
};
